import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Stack, useMediaQuery} from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {DatePicker} from '@mui/x-date-pickers';
import moment from 'moment';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import {useTheme} from '@mui/material/styles';
import {useLocation} from 'react-router-dom';

const Date = ({
                  index,
                  componentId,
                  fieldKey,
                  value,
                  onChange,
                  descriptionHtml,
                  onDescriptionHtmlChange,
                  commitDescriptionHtml,
                  enableValidation,
                  isEditMode,
                  isFormArchived,
                  readOnly,
                  placeholderValues,
                  accessLevel,
                  submitMode,
                  error,
                  minAge,
                  audience,
                  recipientType,
                  linkedToStudents,
                  linkedToEmployees,
              }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const [minAgeEnabled, setMinAgeEnabled] = useState(!!minAge);
    const [isOpen, setIsOpen] = useState(false);
    const datePickerRef = useRef(null);
    const theme = useTheme();
    const location = useLocation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isEmbedded = new URLSearchParams(location.search).get('embedded') === 'true';
    const desktopModeMediaQuery = isEmbedded ? '@media (min-width: 0px)' : '@media (pointer: fine)';
    const embeddedSmallScreenDateSlotProps = isEmbedded && isSmallScreen
        ? {
            popper: {
                placement: 'bottom',
                modifiers: [
                    {
                        name: 'flip',
                        enabled: true,
                        options: {
                            fallbackPlacements: ['top', 'bottom-start', 'bottom-end']
                        }
                    },
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                            altAxis: true,
                            tether: true,
                            padding: 8
                        }
                    }
                ]
            },
            desktopPaper: {
                sx: {
                    maxWidth: 'calc(100vw - 16px)'
                }
            }
        }
        : {};

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    useEffect(() => {
        setMinAgeEnabled(minAge != null);
    }, [minAge]);

    const handleDraftChange = useCallback(
        (nextDescriptionHtml) => {
            setDraftDescriptionHtml(nextDescriptionHtml);
            onDescriptionHtmlChange(nextDescriptionHtml);
        },
        [onDescriptionHtmlChange]
    );

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const formatDate = (date) => {
        return moment(date).format('D MMM YYYY');
    };

    const handleMinAgeCheckbox = (e) => {
        const checked = e.target.checked;
        setMinAgeEnabled(checked);

        if (!checked) {
            onChange({index, minAge: null});
        } else {
            const valueToSet = minAge || '1';
            onChange({
                index,
                minAge: Number(valueToSet)
            });
        }
    };

    const handleMinAgeChange = (e) => onChange({index, minAge: e.target.value});

    const handleOpen = () => {
        setIsOpen(true);
        if (!isSmallScreen) {
            setTimeout(() => {
                if (datePickerRef.current) {
                    datePickerRef.current.scrollIntoView({
                        block: 'center',
                        inline: 'nearest'
                    });
                }
            }, 10);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <Stack flexDirection="column" gap={2}>
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            placeholderValues={placeholderValues}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        <Stack flexDirection="column">
                            <RequiredCheckbox
                                enableValidation={enableValidation}
                                index={index}
                                onChange={onChange}
                                isFormArchived={isFormArchived || !!fieldKey}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={minAgeEnabled}
                                        onChange={handleMinAgeCheckbox}
                                        disabled={!!fieldKey}
                                        color="primary"
                                    />
                                }
                                label="Enable min age validation"
                            />
                            {minAgeEnabled && (
                                <TextField
                                    disabled={!!fieldKey}
                                    label="Min age (years)"
                                    type="number"
                                    inputProps={{min: 1}}
                                    value={minAge}
                                    onChange={handleMinAgeChange}
                                    sx={{mt: 2}}
                                    error={!!error}
                                    helperText={error}
                                />
                            )}
                        </Stack>
                    </Stack>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            submitMode={submitMode}
                        />
                        {!readOnly
                            ? (
                                <DatePicker
                                    key={componentId}
                                    ref={datePickerRef}
                                    desktopModeMediaQuery={desktopModeMediaQuery}
                                    open={isOpen}
                                    onOpen={handleOpen}
                                    onClose={handleClose}
                                    value={value ? moment(value) : null}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!error,
                                            helperText: error
                                        },
                                        field: {readOnly: true},
                                        ...embeddedSmallScreenDateSlotProps,
                                    }}
                                    format="D MMM YYYY"
                                    required={enableValidation}
                                    onChange={(newValue) => {
                                        onChange({
                                            id: componentId,
                                            value: formatDate(newValue)
                                        });
                                    }}
                                    onAccept={() => {
                                        setIsOpen(false);
                                    }}
                                    disabled={!submitMode}
                                    maxDate={
                                        minAge !== null && minAge !== undefined
                                            ? moment().subtract(minAge, 'years')
                                            : undefined
                                    }
                                />
                            )
                            : (
                                <Typography
                                    sx={{
                                        width: {
                                            xs: '80vw',
                                            sm: '60vw',
                                            md: '45vw',
                                            lg: '45vw',
                                            xl: '45vw',
                                        },
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                    }}
                                >
                                    {value && formatDate(value)}
                                </Typography>
                            )}
                    </>
                )}
        </Box>
    );
};

Date.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fieldKey: PropTypes.string,
    value: PropTypes.string,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func,
    commitDescriptionHtml: PropTypes.func,
    onChange: PropTypes.func,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    error: PropTypes.string,
    minAge: PropTypes.number,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
};

export default Date;
